import type { TRecordType } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { CSSProperties, memo, useMemo } from 'react';
import styles from './CardTable.module.scss';
import { Cell, FillerCell } from './Cell';
import type { CardColumn, CardHeader, TColumnsPerRow } from './type';
import { getFillerCount, renderCellContent, renderColumnTitle } from './util';

const cx = classnames.bind(styles);

export type Props<RecordType extends TRecordType> = {
  columns: CardColumn<RecordType>[];
  record: RecordType;
  index: number;
  total: number;
  columnsPerRow: TColumnsPerRow;
  /**
   * Passed as a function rather than a computed object so this component stays
   * memoizable — calling it in the parent would hand down a fresh object on
   * every render and defeat the memo.
   */
  header?: (record: RecordType, index: number, total: number) => CardHeader;
  stickyHeader?: boolean;
  testId?: string;
  /**
   * Virtualization plumbing. `start` is the vertical offset (px) of this card
   * within the virtual list; when set the card is absolutely positioned so the
   * list can render only the visible window. `measureRef`/`virtualIndex` let
   * the virtualizer observe this card's real height (cards vary in height, so
   * the estimate is corrected after mount). Kept as primitives + a stable
   * callback ref so the memo below still holds across scroll re-renders.
   */
  start?: number;
  measureRef?: (node: Element | null) => void;
  virtualIndex?: number;
};

/** Renders one set of columns as `Cell`s (shared by the grouped and ungrouped paths). */
const renderCells = <RecordType extends TRecordType>(
  columns: CardColumn<RecordType>[],
  record: RecordType,
  index: number
) =>
  columns.map((column, columnIndex) => (
    <Cell
      key={
        column.key ??
        (column.dataIndex !== undefined
          ? String(column.dataIndex)
          : columnIndex)
      }
      label={renderColumnTitle(column)}
      value={renderCellContent(column, record, index)}
      align={column.align === 'right' ? 'right' : 'left'}
      unit={column.unit}
      caption={column.caption?.(record, index)}
      help={column.help}
    />
  ));

/** A single record rendered as a card. Internal to CardTable. */
const _RecordCard = <RecordType extends TRecordType>({
  columns,
  record,
  index,
  total,
  columnsPerRow,
  header,
  stickyHeader,
  testId,
  start,
  measureRef,
  virtualIndex,
}: Props<RecordType>) => {
  // When any column carries a `group`, columns are partitioned into
  // side-by-side sub-grids (e.g. 借方/貸方). A `Map` preserves the column
  // array's insertion order for both group order and in-group order — a
  // plain object would too, but `Map` makes that guarantee explicit.
  const groups = useMemo(() => {
    const hasGroups = columns.some((column) => column.group !== undefined);
    if (!hasGroups) return null;

    const map = new Map<string | undefined, typeof columns>();
    columns.forEach((column) => {
      const key = column.group;
      const existing = map.get(key);
      if (existing) {
        existing.push(column);
      } else {
        map.set(key, [column]);
      }
    });
    return Array.from(map.entries());
  }, [columns]);

  const fillerCount = useMemo(
    () => getFillerCount(columns.length, columnsPerRow),
    [columns.length, columnsPerRow]
  );

  const headerContent = useMemo(
    () => header?.(record, index, total),
    [header, record, index, total]
  );

  // Absolute positioning is how the virtual list stacks only the visible
  // window. We position with `top` rather than `transform: translateY` on
  // purpose: a `transform` on an ancestor establishes a containing block that
  // breaks `position: sticky`, so the sticky card header would stop pinning to
  // the scroll container. `top` costs a little more on the paint path but is
  // what keeps sticky headers working under virtualization.
  const itemStyle = useMemo<CSSProperties | undefined>(
    () =>
      start === undefined
        ? undefined
        : {
            position: 'absolute',
            top: start,
            left: 0,
            width: '100%',
          },
    [start]
  );

  return (
    <li
      ref={measureRef}
      data-index={virtualIndex}
      className={cx('item')}
      style={itemStyle}
      data-testid={testId}
    >
      <div className={cx('card')}>
        {headerContent && (
          <div className={cx('head', { sticky: stickyHeader })}>
            <span className={cx('head-title')}>{headerContent.title}</span>
            {headerContent.counter && (
              <span className={cx('head-counter')}>
                {headerContent.counter}
              </span>
            )}
            {headerContent.actions && (
              <span className={cx('head-actions')}>
                {headerContent.actions}
              </span>
            )}
          </div>
        )}
        {groups ? (
          <div className={cx('groups')}>
            {groups.map(([groupKey, groupColumns]) => (
              <div className={cx('group')} key={groupKey ?? 'ungrouped'}>
                <dl className={cx('grid')} data-columns={columnsPerRow}>
                  {renderCells(groupColumns, record, index)}
                  {Array.from({
                    length: getFillerCount(groupColumns.length, columnsPerRow),
                  }).map((_, i) => (
                    <FillerCell key={`filler-${i}`} />
                  ))}
                </dl>
              </div>
            ))}
          </div>
        ) : (
          <dl className={cx('grid')} data-columns={columnsPerRow}>
            {renderCells(columns, record, index)}
            {Array.from({ length: fillerCount }).map((_, i) => (
              <FillerCell key={`filler-${i}`} />
            ))}
          </dl>
        )}
      </div>
    </li>
  );
};
_RecordCard.displayName = '_RecordCard';

// A long list re-renders every card whenever CardTable's own state changes
// (e.g. `columnsPerRow` from a consumer resize handler), so memo matters
// here. The assertion restores the generic parameter that memo erases.
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const RecordCard = memo((props) => (
  <_RecordCard {...props} />
)) as typeof _RecordCard;
RecordCard.displayName = 'RecordCard';
